import * as React from 'react';
import {Plan} from '../AccountCheckout.type';

interface CheckoutSEOProps {
  plan: Plan;
  accountLabel: string;
  accountDescription?: string;
  accountImage?: string;
  accountWeightedRating?: number | string;
  usersSize?: number;
}

export const CheckoutSEO: React.FC<CheckoutSEOProps> = ({
  plan,
  accountLabel,
  accountDescription,
  accountImage,
  accountWeightedRating,
  usersSize,
}) => {
  const ratingValue =
    accountWeightedRating && typeof accountWeightedRating === 'string'
      ? parseFloat(accountWeightedRating)
      : Number(accountWeightedRating || 0);

  const hasRating = ratingValue > 0 && !isNaN(ratingValue);
  const reviewCount = usersSize && usersSize > 0 ? usersSize : undefined;

  // Schema.org Product/Service com Offer
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: plan.name,
    description: plan.description || `${plan.name} - ${accountLabel}`,
    image: plan.cover?.uri || accountImage || '',
    brand: {
      '@type': 'Brand',
      name: accountLabel,
    },
    offers: {
      '@type': 'Offer',
      url: typeof window !== 'undefined' ? window.location.href : '',
      priceCurrency: 'BRL',
      price: parseFloat(String(plan.value)).toFixed(2),
      priceValidUntil: new Date(
        Date.now() + 30 * 24 * 60 * 60 * 1000,
      ).toISOString(), // 30 dias
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: accountLabel,
        image: accountImage || '',
      },
      ...(plan.installments > 1 && {
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          returnPolicyCategory:
            'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 7,
        },
      }),
    },
    ...(hasRating &&
      reviewCount && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: ratingValue.toFixed(1),
          bestRating: '5',
          worstRating: '1',
          ratingCount: reviewCount,
          reviewCount: reviewCount,
        },
      }),
  };

  // Schema.org para serviço de fitness
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Personal Training & Fitness',
    name: plan.name,
    description: plan.description || `${plan.name} - Treino personalizado`,
    provider: {
      '@type': 'LocalBusiness',
      name: accountLabel,
      description: accountDescription || '',
      image: accountImage || '',
      ...(hasRating &&
        reviewCount && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: ratingValue.toFixed(1),
            bestRating: '5',
            worstRating: '1',
            ratingCount: reviewCount,
            reviewCount: reviewCount,
          },
        }),
    },
    offers: {
      '@type': 'Offer',
      url: typeof window !== 'undefined' ? window.location.href : '',
      priceCurrency: 'BRL',
      price: parseFloat(String(plan.value)).toFixed(2),
      priceValidUntil: new Date(
        Date.now() + 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      availability: 'https://schema.org/InStock',
    },
  };

  // Schema.org Breadcrumb
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: accountLabel,
        item: typeof window !== 'undefined' ? window.location.origin : '',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Planos',
        item:
          typeof window !== 'undefined'
            ? `${window.location.origin}/planos`
            : '',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: plan.name,
        item: typeof window !== 'undefined' ? window.location.href : '',
      },
    ],
  };

  // Schema.org WebPage
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${plan.name} - Checkout - ${accountLabel}`,
    description:
      plan.description ||
      `Adquira o ${plan.name} com pagamento seguro e garantia de 7 dias`,
    url: typeof window !== 'undefined' ? window.location.href : '',
    isPartOf: {
      '@type': 'WebSite',
      name: accountLabel,
      url: typeof window !== 'undefined' ? window.location.origin : '',
    },
    mainEntity: {
      '@id': '#product',
    },
  };

  return (
    <>
      {/* Product/Offer Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(productSchema),
        }}
      />

      {/* Service Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(serviceSchema),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />

      {/* WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(webPageSchema),
        }}
      />

      {/* Meta tags para Open Graph (Facebook, LinkedIn) */}
      <meta property="og:type" content="product" />
      <meta property="og:title" content={`${plan.name} - ${accountLabel}`} />
      <meta
        property="og:description"
        content={
          plan.description ||
          `Adquira o ${plan.name} com pagamento seguro e garantia de 7 dias`
        }
      />
      <meta property="og:image" content={plan.cover?.uri || accountImage} />
      <meta
        property="og:url"
        content={typeof window !== 'undefined' ? window.location.href : ''}
      />
      <meta property="product:price:amount" content={String(plan.value)} />
      <meta property="product:price:currency" content="BRL" />

      {/* Meta tags para Twitter Card */}
      <meta name="twitter:card" content="product" />
      <meta name="twitter:title" content={`${plan.name} - ${accountLabel}`} />
      <meta
        name="twitter:description"
        content={
          plan.description ||
          `Adquira o ${plan.name} com pagamento seguro e garantia de 7 dias`
        }
      />
      <meta name="twitter:image" content={plan.cover?.uri || accountImage} />
      <meta name="twitter:label1" content="Preço" />
      <meta
        name="twitter:data1"
        content={`R$ ${parseFloat(String(plan.value))
          .toFixed(2)
          .replace('.', ',')}`}
      />
      {plan.installments > 1 && (
        <>
          <meta name="twitter:label2" content="Parcelamento" />
          <meta
            name="twitter:data2"
            content={`Até ${plan.installments}x sem juros`}
          />
        </>
      )}

      {/* Meta tags para SEO básico */}
      <meta
        name="description"
        content={
          plan.description ||
          `${plan.name} - Treinamento personalizado com ${accountLabel}. Pagamento seguro e garantia de 7 dias.`
        }
      />
      <meta
        name="keywords"
        content={`${accountLabel}, ${plan.name}, personal trainer, treino personalizado, fitness, academia, musculação, checkout`}
      />

      {/* Canonical URL */}
      <link
        rel="canonical"
        href={typeof window !== 'undefined' ? window.location.href : ''}
      />
    </>
  );
};
